// Component Props Types

import type { ReactNode } from 'react';

import type { ContentType } from '../routes/routesConfig';

// System Message Types
export const SystemMessageType = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info'
} as const;

// Button Component
type ButtonBaseProps = {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  color?: 'primary' | 'secondary';
  fill?: 'solid' | 'outline';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  // ARIA properties
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-pressed'?: boolean | 'mixed';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-disabled'?: boolean;
  'aria-busy'?: boolean;
};

type ButtonWithIcon = ButtonBaseProps & {
  icon: string;
  iconPosition: 'start' | 'end';
};

type ButtonWithoutIcon = ButtonBaseProps & {
  icon?: undefined;
  iconPosition?: never;
};

export type ButtonProps = ButtonWithIcon | ButtonWithoutIcon;

// Error Input Message Component
export interface SystemMessageProps {
  message: string | string[];
  type: 'error' | 'success' | 'warning' | 'info';
  show?: boolean;
  autoHide?: boolean;
  hideDelay?: number;
  onClick?: () => void;
  onHide?: () => void;
}

// Loading Component
export interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
}

// Steper Component
export interface Stepper {
  step: number;
  icon: string;
  label: string;
  subLabel: string;
}

export interface StepperProps {
  currentStep: number;
  steps?: Stepper[];
  sucesso?: boolean;
}

export interface StepperXProps {
  totalSteps: number;
  currentStep: number;
}

// System Container Component
export interface SystemContainerProps {
  children?: ReactNode;
  className?: string;
  loading?: boolean;
}

// Navbar Component
export interface NavbarProps {
  user?: {
    name: string;
    role: string;
  };
  onLogout?: () => void;
  className?: string;
}

// User Profile Component
export interface UserProfileProps {
  user: {
    id: number;
    name: string;
    email: string;
    cpf: string;
    civilName: string;
    companyName: string;
    vertical: string;
    role: string;
    status: string;
  };
  onEdit?: () => void;
  className?: string;
}

// Protected Route Component
export interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'Estipulante' | 'Backoffice';
  fallback?: ReactNode;
}

// Input Components - Base ARIA properties
type InputAriaProps = {
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'false' | 'true' | 'grammar' | 'spelling';
  'aria-required'?: boolean;
  'aria-disabled'?: boolean;
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both';
  'aria-haspopup'?: boolean | 'false' | 'true' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-controls'?: string;
  'aria-owns'?: string;
  'aria-activedescendant'?: string;
  'aria-errormessage'?: string;
};

/** Shared props for CPF / CNPJ / DocumentNumber masked inputs. */
export interface MaskedDocumentInputProps extends InputAriaProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export type CpfInputProps = MaskedDocumentInputProps;
export type CnpjInputProps = MaskedDocumentInputProps;
export type DocumentNumberInputProps = MaskedDocumentInputProps;

// Dropdown Option
export type DropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

// Dropdown Component
export interface DropdownProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  errorText?: string;
  id?: string;
  'data-testid'?: string;
  required?: boolean;
  touched?: boolean;
}

// Forgot Userkey Form Component

export interface ForgotUserkeyFormProps {
  onBackToLogin?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

// BsdsInputEvent Form Component
export interface BsdsInputEvent extends React.FormEvent<HTMLBsdsInputElement> {
  detail?: {
    value: string;
  };
}

// Login Second Step Form Component
/** API from second step coming from parent useLogin (uses the same idPessoa saved in verifyUser) */
export interface LoginSecondStepApi {
  isLoading: boolean;
  error: string | null;
  timeLeft: number;
  isCodeExpired: boolean;
  verificationCode: string[];
  showCodeFields: boolean;
  sendCode: (email: boolean, phone: boolean) => Promise<boolean>;
  sendCodeToContact: (contato: import('../types/apiType').Contato) => Promise<boolean>;
  fetchContacts: () => Promise<import('../types/apiType').Contato[]>;
  validateCode: (code: string[]) => Promise<boolean>;
  resendCode: (email: boolean, phone: boolean) => Promise<boolean>;
  updateCode: (index: number, value: string) => void;
  clearError: () => void;
  resetVerificationState: () => void;
  resetState: () => void;
  contatos: import('../types/apiType').Contato[];
  vinculosRequestFailed?: boolean;
  contatosRequestFailed?: boolean;
  retryBusinessLinks?: () => Promise<boolean>;
  retryLoginContacts?: () => Promise<boolean>;
}

export interface LoginSecondStepFormProps {
  onNext: () => void;
  onError: (error: string) => void;
  onLoadingChange: (loading: boolean) => void;
  onBack?: () => void;
  userId: number;
  maskedEmail?: string | null;
  maskedCellphone?: string | null;
  /** When provided, uses the same useLogin from first step (idPessoa/contacts from verifyUser) */
  secondStepApi?: LoginSecondStepApi;
}

// Verification Method Type
export type VerificationMethod = 'email' | 'phone';

// Navbar Component
export interface NavbarProps {
  onMenuToggle?: () => void;
  isLoggingOut?: boolean;
}

// Pagination Component
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemLabel?: string;
  className?: string;
  maxVisiblePages?: number;
  alwaysShow?: boolean;
}

// Confirm Userkey Component
export interface ConfirmUserkeyProps {
  userkey: string;
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
  label?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  'aria-disabled'?: boolean;
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both';
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-controls'?: string;
  'aria-owns'?: string;
  'aria-activedescendant'?: string;
  'aria-errormessage'?: string;
}

// Userkey Validation Rules Component
export interface UserkeyValidationRulesProps {
  userkey: string;
  confirmUserkey: string;
}


// Radio Component
export interface RadioProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'large';
  id?: string;
  'data-testid'?: string;
  color?: 'primary' | 'secondary';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

// Protected Route Component
export interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

// Sidebar Component
export interface SidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
  currentContent?: ContentType;
  isLoggingOut?: boolean;
}

interface SystemMessageConfirmationBaseProps {
  isOpen: boolean;
  isLoading?: boolean;
  title: string;
  message: string;
  onCancel: () => void;
}

// System Message Confirmation Confirmation Component
export interface SystemMessageConfirmationConfirmationProps extends SystemMessageConfirmationBaseProps {
  type: 'confirmation';
  confirmText: string;
  cancelText: string;
  confirmButtonVariant?: 'primary' | 'danger' | 'secondary';
  onConfirm: () => void;
}
// System Message Confirmation Message Component
export interface SystemMessageConfirmationMessageProps extends SystemMessageConfirmationBaseProps {
  type: 'message';
  buttonText: string;
}

// System Message Confirmation Component
export type SystemMessageConfirmationProps = SystemMessageConfirmationConfirmationProps | SystemMessageConfirmationMessageProps;

// Tooltip Component
export interface TooltipProps {
  label: string;
  children: React.ReactElement<Record<string, unknown>>;
  asWrapper?: keyof React.JSX.IntrinsicElements;
  className?: string;
}

// Visibility Component

export interface VisibilityProps {
  children: ReactNode;
  isActive: boolean;
  className?: string;
  animated?: boolean;
  animationDuration?: number;
  role?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
  'aria-atomic'?: boolean;
  'aria-busy'?: boolean;
  'aria-hidden'?: boolean;
}

// Tab Component

export interface TabItem {
  id: string;
  title: string;
  icon: ReactNode;
  content: ReactNode;
  /** When `false`, the tab does not appear in the navigation (definition remains for future reactivation). */
  visible?: boolean;
}

// Tab Component Props
export interface TabProps {
  tabs: TabItem[];
  defaultTabId?: string;
  className?: string;
  onActiveTabChange?: (tab: TabItem) => void;
}

// Login Form Component
export interface LoginFormProps {
  /** Called after login completed. If result?.redirectToCompanySelection, redirect to /company (vínculos no company store). */
  onSuccess: (result?: import('./hookType').LoginSuccessResult) => void;
  onLoadingChange?: (loading: boolean) => void;
  /** When true, skips the second step (2FA) of the login. */
  skipSecondStep?: boolean;
}